import { useApi } from 'api/api-context';
import panic from 'errors/panic';
import { isString } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  normalizeRevisionPredefinedDataOptions,
  sortRevisionPredefinedDataOptions,
} from 'components/inputs/revision-predefined-data/helpers';
import { RevisionPredefinedDataOption } from 'components/inputs/revision-predefined-data/types';

interface UseRevisionPredefinedDataCatalogProps {
  deviceTypeId: number;
  predefinedDataType: string | number;
  revisionId: number;
}

interface UseRevisionPredefinedDataCatalogResult {
  options: RevisionPredefinedDataOption[];
  loading: boolean;
  predefinedDataTypeName: string;
  predefinedDataTypeId?: number;
  upsertCatalogOption: (
    option: Pick<RevisionPredefinedDataOption, 'value' | 'label' | 'description' | 'origin'>
  ) => void;
}

/**
 * Loads and manages predefined data options for revision fill-out inputs.
 */
export function useRevisionPredefinedDataCatalog({
  deviceTypeId,
  predefinedDataType,
  revisionId,
}: UseRevisionPredefinedDataCatalogProps): UseRevisionPredefinedDataCatalogResult {
  const { getAction } = useApi();
  const [options, setOptions] = useState<RevisionPredefinedDataOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [predefinedDataTypeName, setPredefinedDataTypeName] = useState(
    isString(predefinedDataType) ? predefinedDataType : ''
  );
  const [predefinedDataTypeId, setPredefinedDataTypeId] = useState<number | undefined>(
    isString(predefinedDataType) ? undefined : predefinedDataType
  );

  useEffect(() => {
    const action = getAction('PredefinedDataListRevisionData');
    const getPredefinedDataTypes = getAction('PredefinedDataListTypes');

    const filters: Record<string, string | number> = {
      'deviceTypeId.eq': deviceTypeId,
    };

    if (isString(predefinedDataType)) {
      filters['predefinedDataType.eq'] = predefinedDataType;
    } else {
      filters['predefinedDataTypeId.eq'] = predefinedDataType;
    }

    setLoading(true);

    action({ query: { filters }, parameters: { revisionId: String(revisionId) } })
      .then(async (response) => {
        setOptions(normalizeRevisionPredefinedDataOptions(response));

        if (response[0]?.predefinedDataType) {
          setPredefinedDataTypeName(response[0].predefinedDataType.name);
          setPredefinedDataTypeId(response[0].predefinedDataType.predefinedDataTypeId);
          return;
        }

        const predefinedDataTypes = await getPredefinedDataTypes({
          query: {
            filters: {
              'deviceTypeId.eq': deviceTypeId,
            },
          },
        });

        const matchedType = predefinedDataTypes.find((item) =>
          isString(predefinedDataType)
            ? item.name === predefinedDataType
            : item.predefinedDataTypeId === predefinedDataType
        );

        if (matchedType) {
          setPredefinedDataTypeName(matchedType.name);
          setPredefinedDataTypeId(matchedType.predefinedDataTypeId);
          return;
        }

        setPredefinedDataTypeName(isString(predefinedDataType) ? predefinedDataType : '');
        setPredefinedDataTypeId(isString(predefinedDataType) ? undefined : predefinedDataType);
      })
      .catch(panic)
      .finally(() => setLoading(false));
  }, [deviceTypeId, getAction, predefinedDataType, revisionId]);

  const upsertCatalogOption = useCallback(
    (option: Pick<RevisionPredefinedDataOption, 'value' | 'label' | 'description' | 'origin'>) => {
      const nextOption: RevisionPredefinedDataOption = {
        ...option,
        group: 'Vlastné',
        source: 'catalog',
      };

      setOptions((current) =>
        [
          ...current.filter(
            (item) => item.value.trim().toLocaleLowerCase() !== option.value.trim().toLocaleLowerCase()
          ),
          nextOption,
        ].sort(sortRevisionPredefinedDataOptions)
      );
    },
    []
  );

  return useMemo(
    () => ({ options, loading, predefinedDataTypeName, predefinedDataTypeId, upsertCatalogOption }),
    [loading, options, predefinedDataTypeId, predefinedDataTypeName, upsertCatalogOption]
  );
}
